<script lang="ts" setup>
  import CampaignsTabs from '@/views/Tabs/CampaignsTabs.vue';
  import Views from '@/components/Views/Views.vue';
  import CampaignsFilters from '@/components/Campaigns/CampaignsFilters.vue';
  import CampaignsGrid from '@/components/Campaigns/CampaignsGrid.vue';
  import CampaignsCardsList from '@/components/Campaigns/CampaignsCardsList.vue';
  import { useInfiniteQuery } from '@tanstack/vue-query';
  import { getCampaigns, GetCampaignsParams } from '@/api/campaigns';
  import { useRoute } from 'vue-router/composables';
  import { computed } from 'vue';
  import { useStore } from '@/store';
  import BcSpinner from '@/ui-kit/components/BcSpinner/BcSpinner.vue';
  import CampaignsMultiselectFooter from '@/components/Campaigns/CampaignsMultiselectFooter.vue';
  import { useMultiselect } from '@/composables/useMultiselect';
  import InfiniteScrollTrigger from '@/components/InfiniteScroll/InfiniteScrollTrigger.vue';

  const route = useRoute();
  const store = useStore();

  // Reset both cards and selected cards before starting the query
  store.dispatch('resetCards');
  store.dispatch('setSelectedCards', []);

  // Map route query parameters to API parameters
  const routeQueries = computed<GetCampaignsParams>(() => {
    return {
      status: route.query.status as string || '',
      coach: route.query.coach as string || '',
      name: route.query.search as string || '', // API expects 'name', but we use 'search' in routes
      campaignType: route.query['campaign-type'] as string || '', // API expects 'campaignType', but we use 'campaign-type' in routes
    };
  });

  interface CampaignsPage {
    items: CampaignCard[];
    cursor?: string;
  }

  interface InfiniteCampaignsData {
    pages: CampaignsPage[];
    pageParams: (string | undefined)[];
  }

  type CampaignCard = {
    _id: string;
    [key: string]: any;
  }

  const { selectedCards, setSelectedCards, resetSelectedCards, getIsSelectedStatus } = useMultiselect();

  // Add typed handler for card selection
  function handleCardSelection(card: CampaignCard, value: boolean) {
    setSelectedCards(value, card);
  }

  store.dispatch('resetCards');
  resetSelectedCards();

  const {
    data: campaignsPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery<CampaignsPage, Error, InfiniteCampaignsData>({
    queryKey: ['campaigns', routeQueries],
    queryFn: async ({ pageParam }) => {
      // Only reset cards on the first page fetch

      const response = await getCampaigns({
        ...routeQueries.value,
        cursor: pageParam as string | undefined,
      });
      // The API returns a paginated response with items
      const paginatedData = response.data as unknown as CampaignsPage;
      await store.dispatch('setCards', paginatedData.items);
      return paginatedData;
    },
    getNextPageParam: (lastPage) => lastPage.cursor ?? undefined,
    initialPageParam: undefined,
  });

  const campaigns = computed(() =>
    campaignsPages.value?.pages?.flatMap((page: CampaignsPage) => page.items) ?? []
  );

  const isAllCardsSelected = computed(() => 
    campaigns.value.length > 0 && campaigns.value.length === selectedCards.value.length
  );

  function handleSelectAllCampaign(value: boolean) {
    const selectedCards = value ? campaigns.value : [];
    store.dispatch('setSelectedCards', selectedCards);
  }
  
  // Check if we're on the statistics page
  const isStatisticsRoute = computed(() => route.path.includes('/campaigns/statistics'));
</script>

<template>
  <views class="database-campaigns flex w-full shrink-0 flex-col !items-start overflow-auto">
    <div class="flex w-full min-w-[1180px]">
      <CampaignsTabs>
        <CampaignsFilters/>
      </CampaignsTabs>
    </div>
    
    <!-- Only show campaigns content if not on statistics page -->
    <template v-if="isStatisticsRoute">
      <RouterView/>
    </template>
    <template v-else>
      <div class="flex w-full min-w-[1180px] flex-col px-[30px]">
        <RouterView />
        <CampaignsGrid
          :is-checked="isAllCardsSelected"
          @checked="handleSelectAllCampaign"/>
        <div
          v-if="isLoading && !campaigns.length"
          class="flex w-full justify-center py-5">
          <BcSpinner size="large"/>
        </div>
        <div v-else class="flex flex-col">
          <CampaignsCardsList
            :cards="campaigns"
            :has-more="hasNextPage"
            :is-loading-more="isFetchingNextPage"
            @load-more="fetchNextPage"/>
        </div>
      </div>
      <CampaignsMultiselectFooter
        v-if="selectedCards.length"
        class="is-fixed is-bottom is-left is-full-width is-justify-content-center is-align-items-center is-column">
      </CampaignsMultiselectFooter>
    </template>
  </views>
</template>

<style lang="scss" scoped>
  .database-campaigns {
    @include page_grid();
  }
</style>
